// Automatic matching: the available properties a request matches, or the open requests a property matches.
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import { Box, ButtonBase, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { api } from '../api/client';
import type { MatchedProperty, MatchedRequest } from '../api/types';
import { tr } from '../lib/i18n';
import { apiLabel, money, range } from '../lib/labels';
import { RequestStatusChip, SectionCard } from './org';

type Kind = 'request' | 'property';

function useMatches<T>(kind: Kind, id: string) {
  return useQuery({
    queryKey: [kind, id, 'matches'],
    queryFn: () => api<{ items: T[]; canView: boolean }>(`/${kind === 'request' ? 'requests' : 'properties'}/${id}/matches`),
  });
}

const Row = ({ to, children }: { to: string; children: ReactNode }) => (
  <ButtonBase component={RouterLink} to={to} sx={{ display: 'flex', width: '100%', justifyContent: 'flex-start', textAlign: 'start', gap: 1.5, p: 1, borderRadius: 2, '&:hover': { bgcolor: 'action.hover' } }}>
    {children}
  </ButtonBase>
);

const Over = ({ pct }: { pct: number | null }) =>
  pct ? <Chip size="small" color="warning" variant="outlined" label={tr(`فوق الميزانية ${pct}%`, `${pct}% over budget`)} /> : null;

/** Features never exclude a match; how many of the wanted ones are there helps choose. */
const Features = ({ got, want }: { got: number; want: number }) =>
  want > 0 ? (
    <Typography variant="caption" color={got === want ? 'success.main' : 'text.secondary'} sx={{ whiteSpace: 'nowrap' }}>
      {tr(`المميزات ${got}/${want}`, `Features ${got}/${want}`)}
    </Typography>
  ) : null;

const m2 = (v: string | null) => (v ? `${Number(v).toLocaleString('en-US')} m²` : null);

function Empty({ text }: { text: string }) {
  return <Typography color="text.secondary" variant="body2" sx={{ py: 1.5, textAlign: 'center' }}>{text}</Typography>;
}

/** On a request: available properties it matches. */
export function MatchingPropertiesCard({ requestId }: { requestId: string }) {
  const q = useMatches<MatchedProperty>('request', requestId);
  const items = q.data?.items ?? [];
  if (q.data && !q.data.canView) return null;
  return (
    <SectionCard title={`${tr('عقارات مطابقة', 'Matching properties')}${q.data ? ` (${items.length})` : ''}`}>
      {q.isLoading ? <Skeleton height={64} /> : !items.length ? <Empty text={tr('لا يوجد عقار متاح يطابق هذا الطلب حالياً — سيصلك إشعار عند إضافة عقار مطابق.', 'No available property matches this request yet — you will be notified when one is added.')} /> : (
        <Stack spacing={0.5}>
          {items.map((p) => (
            <Row key={p.id} to={`/properties/${p.id}`}>
              <Box sx={{ width: 64, height: 48, borderRadius: 1.5, overflow: 'hidden', bgcolor: 'action.hover', flexShrink: 0, display: 'grid', placeItems: 'center' }}>
                {p.cover_url ? <Box component="img" src={p.cover_url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <HomeWorkOutlinedIcon color="disabled" />}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{p.ref_no} · {apiLabel(p.property_type_name)}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {[
                    [p.governorate_name, p.area_name, p.neighborhood_name].filter(Boolean).join(' / '),
                    m2(p.area) ?? m2(p.building_area) ?? m2(p.land_area),
                    p.rooms != null ? tr(`${p.rooms} غرف نوم`, `${p.rooms} bedrooms`) : null,
                  ].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
                <Typography variant="body2" fontWeight={600}>{money(p.price, p.currency)}</Typography>
                <Over pct={p.over_budget_percent} />
                <Features got={p.features_matched} want={p.features_wanted} />
              </Stack>
            </Row>
          ))}
        </Stack>
      )}
    </SectionCard>
  );
}

/** On a property: open requests it matches. */
export function MatchingRequestsCard({ propertyId }: { propertyId: string }) {
  const q = useMatches<MatchedRequest>('property', propertyId);
  const items = q.data?.items ?? [];
  if (q.data && !q.data.canView) return null;
  return (
    <SectionCard title={`${tr('طلبات مطابقة', 'Matching requests')}${q.data ? ` (${items.length})` : ''}`}>
      {q.isLoading ? <Skeleton height={64} /> : !items.length ? <Empty text={tr('لا يوجد طلب مفتوح يطابق هذا العقار حالياً.', 'No open request matches this property yet.')} /> : (
        <Stack spacing={0.5}>
          {items.map((r) => (
            <Row key={r.id} to={`/requests/${r.id}`}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {r.ref_no} · {apiLabel(r.request_type_name)}{r.contact_name ? ` · ${r.contact_name}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {tr('الميزانية', 'Budget')}: {range(r.budget_min, r.budget_max, ` ${r.currency}`)}
                  {r.locations ? ` · ${r.locations}` : ''}
                  {r.assigned_to_name ? ` · ${r.assigned_to_name}` : ''}
                </Typography>
              </Box>
              <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
                <RequestStatusChip status={r.status} />
                <Over pct={r.over_budget_percent} />
                <Features got={r.features_matched} want={r.features_wanted} />
              </Stack>
            </Row>
          ))}
        </Stack>
      )}
    </SectionCard>
  );
}
